import { storeToRefs } from "pinia";
import { computed, reactive, ref } from "vue";

import useUserStore from "shared/stores/user";

import {
  type AIAssistantOptions,
  type AIAssistantStream,
  type AIAssistantStreamData,
  useAIAssistantStream,
} from "./useAIAssistantStream";

export interface AIAssistantUserMessage {
  message: string;
  options?: never;
  role: "user";
  stream?: never;
}

export interface AIAssistantModelMessage {
  message: string;
  options?: never;
  role: "model";
  stream: AIAssistantStream;
}

export interface AIAssistantSuggestMessageOption {
  icon?: string;
  iconClass?: string;
  label: string;
  newIcon?: boolean;
  tooltipText: string;
}

export interface AIAssistantSuggestMessage {
  message: string;
  options: AIAssistantSuggestMessageOption[];
  role: "suggest";
  stream?: never;
}

export type AIAssistantMessage =
  | AIAssistantModelMessage
  | AIAssistantSuggestMessage
  | AIAssistantUserMessage;

export function isAIAssistantUserMessage(
  message: AIAssistantMessage
): message is AIAssistantModelMessage {
  return message.role === "user";
}

export function isAIAssistantModelMessage(
  message: AIAssistantMessage
): message is AIAssistantModelMessage {
  return message.role === "model";
}

export function isAIAssistantSuggestMessage(
  message: AIAssistantMessage
): message is AIAssistantSuggestMessage {
  return message.role === "suggest";
}

export function useAIAssistant(options: AIAssistantOptions) {
  const userStore = useUserStore();
  const { betaEnabled } = storeToRefs(userStore);

  const messages = ref<AIAssistantMessage[]>([]);

  const data = ref<AIAssistantStreamData>({
    ai_feature_configuration_id: null,
    group_id: null,
    source: options.source || "unknown",
  });

  const loading = computed(() => {
    const lastModelMessage = messages.value
      .toReversed()
      .find((message) => isAIAssistantModelMessage(message));

    if (!lastModelMessage) return false;

    return lastModelMessage.stream.loading;
  });

  function add(message: AIAssistantMessage | AIAssistantMessage[]) {
    messages.value = messages.value.concat(message);
  }

  function submit(prompt: string) {
    if (!prompt.trim()) return;

    add([
      {
        role: "user",
        message: prompt,
      },
      {
        role: "model",
        message: "",
        stream: reactive(
          useAIAssistantStream(data, prompt, {
            beta: betaEnabled.value,
            ...options,
          })
        ),
      },
    ]);
  }

  function reset() {
    data.value = {
      ai_feature_configuration_id: null,
      group_id: null,
      source: "",
    };

    messages.value = [];
  }

  return {
    data,
    loading,
    messages,

    add,
    reset,
    submit,
  };
}
